import React from 'react';
import { matchPath, Route, RouteProps, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { CancelToken } from 'axios';
import { AppBar, Tabs } from '@material-ui/core';
import { TabContainer, TabContent } from '@styles/common';
import { PlayerData } from '@domain/types/player/player.data';
import { useFormData } from '@domain/hooks/use.form.data';
import { useAuth } from '@domain/auth/auth.hook';
import { Loading } from '@components/loading';
import { ErrorDisplay } from '@components/error/error.display';
import { Link } from '@components/link';
import { PlayerDataProps } from '@screens/players/types/player.data.props';
import PlayerListItem from '@screens/players/player.list.item';
import { PlayerTab } from '@screens/players/player.details.styles';
import ButtonBar from '@screens/players/button.bar/button.bar';
import PlayerInfo from '@screens/players/player.details.tabs/player.info';
import PlayerTourneys from '@screens/players/player.details.tabs/player.tourneys';
import DailyMissions from '@screens/players/player.details.tabs/daily.missions/player.daily.missions';
import IFlags from '@screens/players/player.details.tabs/player.iflags';
import PlayerSessionsTab from '@screens/players/player.details.tabs/sessions/player.session.tab';
import PlayerSeasons from '@screens/players/player.details.tabs/player.seasons';
import PlayerNotes from '@screens/players/player.details.tabs/notes/player.notes';
import { gameTypeFilter } from '@data/menu.options.data';
import PlayerPayments from '@screens/players/player.details.tabs/payments/player.payments';
import { GameType } from '@domain/game.selection/types/game.type';
import Collusions from '@screens/players/player.details.tabs/player.collusions';
import PlayerDiffs from '@screens/players/player.details.tabs/diffs/player.diffs';
import Hardware from '@screens/players/player.details.tabs/player.hardware';
import PlayerMassCollections from '@screens/players/player.details.tabs/player.mass.collections';
import PlayerMessages from '@screens/players/player.details.tabs/player.messages';
import DailyDeals from '@screens/players/player.details.tabs/player.daily.deals';
import PlayerVip from '@screens/players/player.details.tabs/player.vip';
import Promos from './player.details.tabs/promos/player.promos';
import EmailDeliverability from './player.details.tabs/player.email.deliverability';

function a11yProps(index: number) {
  return {
    id: `player-tab-${index}`,
    'aria-controls': `player-detail-${index}`,
  };
}

interface SectionsData {
  id: string;
  title: string;
  component: React.ComponentType | React.ComponentType<any>;
  path?: string;
  condition?(): boolean;
}
function useTabSections(): SectionsData[] {
  // eslint-disable-next-line no-use-before-define
  return tabSectionsData.filter((o) => !o.condition || o.condition());
}

const tabSectionsData: SectionsData[] = [
  { id: 'info', title: 'Info', component: PlayerInfo },
  { id: 'sessions', title: 'Sessions', component: PlayerSessionsTab, path: 'sessions/:sessionId?/:gameSessionId?' },
  { id: 'diffs', title: 'Diffs', component: PlayerDiffs },
  { id: 'payments', title: 'Payments', component: PlayerPayments },
  { id: 'masscollections', title: 'Mass Collections', component: PlayerMassCollections },
  { id: 'messages', title: 'Messages', component: PlayerMessages },
  { id: 'vip', title: 'VIP', component: PlayerVip },
  { id: 'dailydeals', title: 'Daily Deals', component: DailyDeals, condition: () => gameTypeFilter(GameType.Praia) },
  { id: 'tourneys', title: 'Tourneys', component: PlayerTourneys },
  { id: 'iflags', title: 'IFlags', component: IFlags },
  { id: 'emaildeliverability', title: 'Email Deliverability', component: EmailDeliverability },
  { id: 'dailymissions', title: 'Daily Missions', component: DailyMissions },
  { id: 'promos', title: 'Promos', component: Promos, condition: () => gameTypeFilter(GameType.Praia) },
  { id: 'collusions', title: 'Collusions', component: Collusions },
  { id: 'hardware', title: 'Hardware', component: Hardware },
  { id: 'notes', title: 'Notes', component: PlayerNotes },
  { id: 'seasons', title: 'Seasons', component: PlayerSeasons, condition: () => gameTypeFilter(GameType.Rex) },
];

type TabRouteProps = PlayerDataProps &
  Omit<RouteProps, 'component'> & {
    selectedTab: number;
    index: number;
    component: React.ComponentType | React.ComponentType<any> | React.ComponentType<PlayerDataProps>;
  };

const TabRoute = ({ selectedTab, index, player, component: Component, ...rest }: TabRouteProps) => {
  return (
    <Route
      {...rest}
      render={() => {
        return (
          <div role='tabpanel' hidden={selectedTab !== index} id={`player-detail-${index}`} aria-labelledby={`player-tab-${index}`}>
            <Component player={player} />
          </div>
        );
      }}
    />
  );
};

interface PlayerDetailsParams {
  pid: string;
}

const PlayerDetails = () => {
  const { pid } = useParams<PlayerDetailsParams>();
  const { authorizedServices } = useAuth();
  const history = useHistory();
  const { path, url } = useRouteMatch();

  const tabSections = useTabSections();

  async function runSearch(requestPid: string, cancelToken: CancelToken | undefined) {
    const response = await authorizedServices
      .get<PlayerData[]>('/admin/player/search', { params: { field: 'pid', value: requestPid }, cancelToken })
      .catch((error) => {
        if (error && error.response && error.response.status === 404) throw new Error('No data found');
        throw error;
      });
    return response.data;
  }

  const formData = useFormData(pid, runSearch, {
    autoRequest: true,
    clearResponseOnRequest: false,
  });

  const { request, response, loading, error } = formData;
  const player = response?.[0];

  const selectedTab = Math.max(
    0,
    tabSections.findIndex((section) =>
      matchPath(history.location.pathname, {
        path: `${url}/${section.id}`,
      }),
    ),
  );

  // eslint-disable-next-line @typescript-eslint/ban-types
  function handleChange(event: React.ChangeEvent<{}>, newTab: number) {
    event.preventDefault();
    const section = tabSections[newTab];
    if (!section) return;
    history.push(`${url}/${section.id}`);
  }

  function updateData() {
    request(pid);
  }

  if (!player && loading) return <Loading loading />;
  if (error) return <ErrorDisplay error={error} />;
  if (!player) return null;

  return (
    <>
      <PlayerListItem player={player}>
        <ButtonBar player={player} loading={loading} onChange={updateData} />
      </PlayerListItem>

      <TabContainer>
        <AppBar position='static'>
          <Tabs value={selectedTab} onChange={handleChange} variant='scrollable' scrollButtons='auto' aria-label='Player details sections'>
            {tabSections.map((section, index) => (
              <PlayerTab
                key={section.id}
                value={index}
                component={Link}
                avoidPrefix
                to={`${url}/${section.id}`}
                label={section.title}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </AppBar>

        <TabContent>
          <Switch>
            {tabSections.map(
              (section, index) =>
                index > 0 && (
                  <TabRoute
                    key={section.id}
                    path={`${path}/${section.path || section.id}`}
                    selectedTab={selectedTab}
                    index={index}
                    player={player}
                    component={section.component}
                  />
                ),
            )}
            <TabRoute path='*' selectedTab={selectedTab} index={0} player={player} component={PlayerInfo} />
          </Switch>
        </TabContent>
      </TabContainer>
    </>
  );
};

export default PlayerDetails;
